import { useState, useEffect, useRef, useCallback } from "react";
import { useMeeting } from "@videosdk.live/react-sdk";
import { sendAudioToTranscribe } from "../hooks/socketManager";

const useVoiceActivatedRecording = ({ isBot }) => {
  const { meetingId } = useMeeting();

  const [isRecording, setIsRecording] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationIdRef = useRef(null);
  const audioContextRef = useRef(null);

  const skipNextSendRef = useRef(false);
  const pendingRestartRef = useRef(false);

  // Used to prevent multiple recovery calls at the same time
  const recoveringRef = useRef(false);

  const getSupportedMimeType = () => {
    const mimeTypes = [
      "audio/webm;codecs=opus",
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "audio/mpeg",
      "audio/wav",
    ];

    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return "";
  };

  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current || !isRecording) {
      setAudioLevel(0);
      return;
    }

    const dataArray = new Uint8Array(analyserRef.current.fftSize);

    analyserRef.current.getByteTimeDomainData(dataArray);

    const rms = Math.sqrt(
      dataArray.reduce(
        (sum, val) => sum + (val - 128) ** 2,
        0
      ) / dataArray.length
    );

    setAudioLevel(Math.min(rms / 10, 10));

    if (isRecording) {
      animationIdRef.current =
        requestAnimationFrame(monitorAudioLevel);
    }
  }, [isRecording]);

  /**
   * Completely destroy the old microphone pipeline.
   *
   * This is important for Edge after headphone/device disconnect.
   */
  const cleanupAudioPipeline = useCallback(() => {
    console.log("🧹 Cleaning up old audio pipeline");

    // Stop animation
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }

    // Disconnect/close old AudioContext
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== "closed") {
          audioContextRef.current.close();
        }
      } catch (error) {
        console.warn(
          "Failed to close AudioContext:",
          error
        );
      }

      audioContextRef.current = null;
    }

    // Stop old microphone tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.warn(
            "Failed to stop audio track:",
            error
          );
        }
      });

      streamRef.current = null;
    }

    // Remove old recorder
    const oldRecorder = mediaRecorderRef.current;

    if (oldRecorder) {
      try {
        oldRecorder.ondataavailable = null;
        oldRecorder.onstop = null;
        oldRecorder.onerror = null;
        oldRecorder.onstart = null;
      } catch (error) {
        console.warn(
          "Failed to clean MediaRecorder handlers:",
          error
        );
      }

      mediaRecorderRef.current = null;
    }

    analyserRef.current = null;
    audioChunksRef.current = [];

    setAudioLevel(0);
  }, []);

  /**
   * Create a fresh microphone stream + MediaRecorder.
   */
  const createAudioPipeline = useCallback(async () => {
    console.log("🎤 Creating fresh microphone pipeline...");

    const stream =
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

    streamRef.current = stream;

    const audioTrack = stream.getAudioTracks()[0];

    console.log("🎤 New microphone track:", {
      label: audioTrack?.label,
      readyState: audioTrack?.readyState,
      enabled: audioTrack?.enabled,
      muted: audioTrack?.muted,
    });

    const mimeType = getSupportedMimeType();

    let mediaRecorder;

    try {
      mediaRecorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
      });
    } catch (error) {
      console.error(
        "MediaRecorder creation failed:",
        error
      );

      stream.getTracks().forEach((track) => track.stop());

      streamRef.current = null;

      throw error;
    }

    /**
     * AudioContext / analyser
     */
    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;

    const audioContext = new AudioContextClass();

    audioContextRef.current = audioContext;

    const source =
      audioContext.createMediaStreamSource(stream);

    const analyser =
      audioContext.createAnalyser();

    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.3;

    source.connect(analyser);

    analyserRef.current = analyser;

    /**
     * Receive recorder chunks.
     */
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    /**
     * Recorder stopped.
     */
    mediaRecorder.onstop = async () => {
      const chunksFromThisTake =
        audioChunksRef.current;

      audioChunksRef.current = [];

      const shouldSkipSend =
        skipNextSendRef.current;

      const shouldRestart =
        pendingRestartRef.current;

      skipNextSendRef.current = false;
      pendingRestartRef.current = false;

      if (shouldSkipSend) {
        if (shouldRestart) {
          try {
            audioChunksRef.current = [];

            if (mediaRecorder.state === "inactive") {
              mediaRecorder.start(100);

              if (
                audioContextRef.current?.state ===
                "suspended"
              ) {
                await audioContextRef.current.resume();
              }

              return;
            }
          } catch (error) {
            console.error(
              "Failed to restart recording after discard:",
              error
            );
          }
        }

        setIsRecording(false);
        setAudioLevel(0);

        if (animationIdRef.current) {
          cancelAnimationFrame(
            animationIdRef.current
          );
          animationIdRef.current = null;
        }

        return;
      }

      setIsRecording(false);
      setAudioLevel(0);

      if (animationIdRef.current) {
        cancelAnimationFrame(
          animationIdRef.current
        );
        animationIdRef.current = null;
      }

      if (chunksFromThisTake.length === 0) {
        return;
      }

      const blob = new Blob(
        chunksFromThisTake,
        {
          type:
            mediaRecorder.mimeType ||
            mimeType ||
            "audio/webm",
        }
      );

      if (blob.size < 1000) {
        console.warn(
          "Audio too short/skipped"
        );
        return;
      }

      try {
        const buffer =
          await blob.arrayBuffer();

        sendAudioToTranscribe(buffer);
      } catch (error) {
        console.error(
          "Audio send failed:",
          error
        );
      }
    };

    mediaRecorder.onerror = (event) => {
      console.error(
        "❌ MediaRecorder error:",
        event
      );
    };

    mediaRecorderRef.current =
      mediaRecorder;

    setIsReady(true);

    console.log(
      "✅ Fresh MediaRecorder created",
      {
        state: mediaRecorder.state,
        mimeType: mediaRecorder.mimeType,
        trackState:
          audioTrack?.readyState,
      }
    );

    return mediaRecorder;
  }, []);

  /**
   * Completely recreate microphone after an audio-device change.
   *
   * This is the important Edge recovery logic.
   */
  const recreateAudioPipeline =
    useCallback(async () => {
      if (recoveringRef.current) {
        console.log(
          "Already recovering microphone - skipping"
        );
        return null;
      }

      recoveringRef.current = true;

      try {
        console.log(
          "🔄 Recreating microphone after device change..."
        );

        setIsRecording(false);

        cleanupAudioPipeline();

        // Give Edge a little time to finish
        // switching the audio device.
        await new Promise((resolve) =>
          setTimeout(resolve, 300)
        );

        const recorder =
          await createAudioPipeline();

        console.log(
          "✅ Microphone recovery completed"
        );

        return recorder;
      } catch (error) {
        console.error(
          "❌ Failed to recreate microphone:",
          error
        );

        setIsReady(false);

        return null;
      } finally {
        recoveringRef.current = false;
      }
    }, [
      cleanupAudioPipeline,
      createAudioPipeline,
    ]);

  /**
   * Start recording.
   */
  const startRecording = useCallback(
    async () => {
      let recorder =
        mediaRecorderRef.current;

      if (!recorder || isBot) {
        console.warn(
          "Cannot start recording - recorder unavailable"
        );

        return;
      }

      /**
       * IMPORTANT:
       *
       * Edge can get into this state:
       *
       * recorder.state = "recording"
       * isRecording = false
       *
       * This was exactly what your console showed.
       */
      if (
        recorder.state === "recording" &&
        !isRecording
      ) {
        console.warn(
          "⚠️ MediaRecorder and React state are out of sync. Recovering..."
        );

        recorder =
          await recreateAudioPipeline();

        if (!recorder) {
          return;
        }
      }

      /**
       * Check microphone track.
       */
      const audioTrack =
        streamRef.current?.getAudioTracks()?.[0];

      if (
        !audioTrack ||
        audioTrack.readyState !== "live"
      ) {
        console.warn(
          "⚠️ Microphone track is not live. Recreating..."
        );

        recorder =
          await recreateAudioPipeline();

        if (!recorder) {
          return;
        }
      }

      /**
       * Final recorder-state check.
       */
      if (recorder.state !== "inactive") {
        console.warn(
          "Cannot start recording",
          {
            hasRecorder: !!recorder,
            recorderState: recorder.state,
            isRecording,
            isBot,
          }
        );

        return;
      }

      try {
        audioChunksRef.current = [];

        /**
         * Ensure AudioContext is running.
         */
        if (
          audioContextRef.current?.state ===
          "suspended"
        ) {
          try {
            await audioContextRef.current.resume();
          } catch (error) {
            console.warn(
              "Failed to resume AudioContext:",
              error
            );
          }
        }

        recorder.start(100);

        setIsRecording(true);

        cancelAnimationFrame(
          animationIdRef.current
        );

        monitorAudioLevel();

        console.log(
          "🎙️ Recording started successfully"
        );
      } catch (error) {
        console.error(
          "❌ Failed to start recording:",
          error
        );

        /**
         * This handles the exact error you showed:
         *
         * NotSupportedError:
         * Failed to execute 'start' on 'MediaRecorder'
         */
        console.log(
          "🔄 Attempting MediaRecorder recovery..."
        );

        await recreateAudioPipeline();
      }
    },
    [
      isRecording,
      isBot,
      monitorAudioLevel,
      recreateAudioPipeline,
    ]
  );

  /**
   * Stop and send recording.
   */
  const stopAndSendRecording =
    useCallback(async () => {
      const recorder =
        mediaRecorderRef.current;

      if (
        !recorder ||
        recorder.state !== "recording"
      ) {
        return;
      }

      try {
        recorder.stop();
      } catch (error) {
        console.error(
          "Error stopping recorder:",
          error
        );
      }
    }, []);

  /**
   * Discard current recording and restart.
   */
  const discardAndRestartRecording =
    useCallback(() => {
      const recorder =
        mediaRecorderRef.current;

      if (!recorder) {
        return;
      }

      if (recorder.state === "recording") {
        skipNextSendRef.current = true;
        pendingRestartRef.current = true;

        try {
          recorder.stop();
        } catch (error) {
          console.error(
            "Error discarding recording:",
            error
          );

          audioChunksRef.current = [];

          skipNextSendRef.current = false;
          pendingRestartRef.current = false;
        }
      } else {
        audioChunksRef.current = [];
      }
    }, []);

  /**
   * Initial microphone setup.
   */
  useEffect(() => {
    if (isBot) {
      return;
    }

    let cancelled = false;

    const setupRecording = async () => {
      try {
        const recorder =
          await createAudioPipeline();

        if (cancelled) {
          return;
        }

        if (recorder) {
          setIsReady(true);
        }
      } catch (error) {
        console.error(
          "Failed to setup recording:",
          error
        );

        if (!cancelled) {
          setIsReady(false);
        }
      }
    };

    setupRecording();

    return () => {
      cancelled = true;

      if (animationIdRef.current) {
        cancelAnimationFrame(
          animationIdRef.current
        );
      }

      cleanupAudioPipeline();

      setIsReady(false);
      setIsRecording(false);
    };
  }, [
    isBot,
    meetingId,
    createAudioPipeline,
    cleanupAudioPipeline,
  ]);

  /**
   * IMPORTANT:
   *
   * Detect headphone / microphone / audio-device
   * changes.
   *
   * Edge fires this when the headphone is removed.
   */
  useEffect(() => {
    if (isBot) {
      return;
    }

    const handleDeviceChange = async () => {
      console.log(
        "🎧 Audio device changed"
      );

      const stream = streamRef.current;
      const recorder =
        mediaRecorderRef.current;

      const audioTrack =
        stream?.getAudioTracks()?.[0];

      console.log(
        "🎤 Audio state after device change:",
        {
          recorderState:
            recorder?.state,
          isRecording,
          trackState:
            audioTrack?.readyState,
          trackMuted:
            audioTrack?.muted,
          trackEnabled:
            audioTrack?.enabled,
        }
      );

      /**
       * Give Edge time to finish switching
       * audio devices.
       */
      await new Promise((resolve) =>
        setTimeout(resolve, 500)
      );

      const currentTrack =
        streamRef.current
          ?.getAudioTracks()?.[0];

      const currentRecorder =
        mediaRecorderRef.current;

      /**
       * Recover if:
       *
       * - microphone track ended
       * - recorder is stuck recording
       * - recorder disappeared
       */
      const trackIsBroken =
        !currentTrack ||
        currentTrack.readyState !== "live";

      const recorderIsBroken =
        !currentRecorder ||
        (
          currentRecorder.state ===
            "recording" &&
          !isRecording
        );

      if (
        trackIsBroken ||
        recorderIsBroken
      ) {
        console.warn(
          "⚠️ Audio pipeline is broken after device change"
        );

        await recreateAudioPipeline();
      } else {
        console.log(
          "✅ Audio pipeline is still healthy"
        );
      }
    };

    if (
      navigator.mediaDevices &&
      navigator.mediaDevices.addEventListener
    ) {
      navigator.mediaDevices.addEventListener(
        "devicechange",
        handleDeviceChange
      );
    }

    return () => {
      if (
        navigator.mediaDevices &&
        navigator.mediaDevices.removeEventListener
      ) {
        navigator.mediaDevices.removeEventListener(
          "devicechange",
          handleDeviceChange
        );
      }
    };
  }, [
    isBot,
    isRecording,
    recreateAudioPipeline,
  ]);

  /**
   * Passive audio level monitor.
   */
  useEffect(() => {
    let passiveId;

    const updateLevel = () => {
      if (!analyserRef.current) {
        return;
      }

      const dataArray =
        new Uint8Array(
          analyserRef.current.fftSize
        );

      analyserRef.current.getByteTimeDomainData(
        dataArray
      );

      const rms = Math.sqrt(
        dataArray.reduce(
          (sum, val) =>
            sum + (val - 128) ** 2,
          0
        ) / dataArray.length
      );

      const level = Math.min(
        rms / 10,
        1
      );

      setAudioLevel(level);
    };

    /**
     * When recording, main monitor handles it.
     */
    if (isRecording) {
      monitorAudioLevel();
    } else {
      passiveId = setInterval(
        updateLevel,
        200
      );
    }

    return () => {
      if (passiveId) {
        clearInterval(passiveId);
      }
    };
  }, [
    isRecording,
    monitorAudioLevel,
  ]);

  return {
    isRecording,
    isReady,
    audioLevel,
    startRecording,
    stopAndSendRecording,
    discardAndRestartRecording,
    analyserRef,
    audioContextRef,
  };
};

export default useVoiceActivatedRecording;